import React, { useEffect, useMemo, useState } from "react";
import { FaArrowRight, FaCalendarAlt, FaMapMarkerAlt, FaSave, FaUsers, FaWallet } from "react-icons/fa";
import type { TravelCategory } from "../../config/travelpayoutsWidgets";

export type TripProfile = "solo" | "couple" | "family" | "business";

export type ForfyTripPlan = {
  destination: string;
  startDate: string;
  endDate: string;
  profile: TripProfile;
  budget: string;
};

type Props = {
  isEn: boolean;
  activeCategory: TravelCategory;
  initialPlan?: Partial<ForfyTripPlan>;
  onPlanChange: (plan: ForfyTripPlan) => void;
  onContinue: (category: TravelCategory, plan: ForfyTripPlan) => void;
  onCreateAlert: (plan: ForfyTripPlan) => void;
};

const STORAGE_KEY = "forfeo_compare_trip_plan";

const DEFAULT_PLAN: ForfyTripPlan = {
  destination: "",
  startDate: "",
  endDate: "",
  profile: "couple",
  budget: "",
};

export function readSavedTripPlan(): ForfyTripPlan | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ForfyTripPlan>;
    return { ...DEFAULT_PLAN, ...parsed };
  } catch {
    return null;
  }
}

function suggestedCategory(plan: ForfyTripPlan): TravelCategory {
  if (plan.profile === "business") return "flights";
  if (plan.destination && !plan.startDate) return "experiences";
  return "flights";
}

export default function ForfyTripPlanner({
  isEn,
  activeCategory,
  initialPlan,
  onPlanChange,
  onContinue,
  onCreateAlert,
}: Props) {
  const [plan, setPlan] = useState<ForfyTripPlan>(() => ({
    ...DEFAULT_PLAN,
    ...readSavedTripPlan(),
    ...initialPlan,
  }));
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    onPlanChange(plan);
  }, [onPlanChange, plan]);

  const recommendation = useMemo(() => suggestedCategory(plan), [plan]);

  const update = <K extends keyof ForfyTripPlan>(key: K, value: ForfyTripPlan[K]) => {
    setSaved(false);
    setPlan((current) => ({ ...current, [key]: value }));
  };

  const save = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
    } catch {
      // La planification doit rester utilisable même sans stockage local.
    }
    setSaved(true);
  };

  const profiles: Array<{ value: TripProfile; fr: string; en: string }> = [
    { value: "solo", fr: "Solo", en: "Solo" },
    { value: "couple", fr: "Couple", en: "Couple" },
    { value: "family", fr: "Famille", en: "Family" },
    { value: "business", fr: "Affaires", en: "Business" },
  ];

  return (
    <section className="relative -mt-8 mb-10 overflow-hidden rounded-[2.25rem] border border-white/10 bg-white p-5 shadow-2xl shadow-slate-900/15 md:p-8">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500" />
      <div className="grid gap-7 xl:grid-cols-[0.75fr_1.25fr] xl:items-end">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-700">
            {isEn ? "Plan with Forfy" : "Planifier avec Forfy"}
          </p>
          <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950 md:text-4xl">
            {isEn ? "Tell me what you have in mind." : "Dites-moi ce que vous avez en tête."}
          </h2>
          <p className="mt-3 max-w-xl text-sm font-medium leading-6 text-slate-500">
            {isEn
              ? "Your destination, dates, travel profile and budget stay together while you compare each essential."
              : "Votre destination, vos dates, votre profil de voyage et votre budget restent regroupés pendant que vous comparez chaque essentiel."}
          </p>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <label className="rounded-2xl border border-slate-200 bg-slate-50 p-3 xl:col-span-2">
            <span className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-slate-500"><FaMapMarkerAlt /> {isEn ? "Destination" : "Destination"}</span>
            <input
              value={plan.destination}
              onChange={(event) => update("destination", event.target.value)}
              placeholder={isEn ? "Paris, Montréal..." : "Paris, Montréal..."}
              className="w-full bg-transparent text-sm font-bold text-slate-950 outline-none"
            />
          </label>

          <label className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <span className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-slate-500"><FaCalendarAlt /> {isEn ? "Departure" : "Départ"}</span>
            <input type="date" value={plan.startDate} onChange={(event) => update("startDate", event.target.value)} className="w-full bg-transparent text-sm font-bold text-slate-950 outline-none" />
          </label>

          <label className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <span className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-slate-500"><FaCalendarAlt /> {isEn ? "Return" : "Retour"}</span>
            <input type="date" min={plan.startDate || undefined} value={plan.endDate} onChange={(event) => update("endDate", event.target.value)} className="w-full bg-transparent text-sm font-bold text-slate-950 outline-none" />
          </label>

          <label className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <span className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-slate-500"><FaUsers /> {isEn ? "Profile" : "Profil"}</span>
            <select value={plan.profile} onChange={(event) => update("profile", event.target.value as TripProfile)} className="w-full bg-transparent text-sm font-bold text-slate-950 outline-none">
              {profiles.map((profile) => <option key={profile.value} value={profile.value}>{isEn ? profile.en : profile.fr}</option>)}
            </select>
          </label>

          <label className="rounded-2xl border border-slate-200 bg-slate-50 p-3 xl:col-span-2">
            <span className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-slate-500"><FaWallet /> {isEn ? "Budget" : "Budget"}</span>
            <input inputMode="numeric" value={plan.budget} onChange={(event) => update("budget", event.target.value.replace(/[^0-9]/g, ""))} placeholder={isEn ? "Example: 1500" : "Exemple : 1500"} className="w-full bg-transparent text-sm font-bold text-slate-950 outline-none" />
          </label>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="text-sm font-semibold text-slate-500">
          {plan.destination ? (
            <>{isEn ? "Forfy is preparing" : "Forfy prépare"} <strong className="text-slate-950">{plan.destination}</strong>{plan.budget ? ` · ${plan.budget} $` : ""}</>
          ) : (
            isEn ? "Add a destination to personalize the journey." : "Ajoutez une destination pour personnaliser le parcours."
          )}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button type="button" onClick={save} className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-xs font-black uppercase tracking-wider text-slate-700 transition hover:border-cyan-400">
            <FaSave /> {saved ? (isEn ? "Saved" : "Enregistré") : (isEn ? "Save my plan" : "Enregistrer mon plan")}
          </button>
          <button type="button" onClick={() => onCreateAlert(plan)} className="inline-flex items-center justify-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-5 py-3 text-xs font-black uppercase tracking-wider text-cyan-800 transition hover:bg-cyan-100">
            {isEn ? "Watch prices" : "Surveiller les occasions"}
          </button>
          <button type="button" onClick={() => onContinue(activeCategory || recommendation, plan)} className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-xs font-black uppercase tracking-wider text-white transition hover:-translate-y-0.5 hover:bg-blue-700">
            {isEn ? "Continue comparing" : "Continuer la comparaison"} <FaArrowRight />
          </button>
        </div>
      </div>
    </section>
  );
}
